export type ParsedTransaction = {
  date: string;
  label: string;
  amount: number;
};

// Utility function for parsing CSV lines - reserved for future use
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function parseCSVLine(line: string, separator: string = ';'): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === separator && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }

  result.push(current);
  return result;
}

function parseCSVRows(csvContent: string, separator: string = ';'): string[][] {
  const rows: string[][] = [];
  let currentRow: string[] = [];
  let currentField = '';
  let inQuotes = false;

  for (let i = 0; i < csvContent.length; i++) {
    const char = csvContent[i];
    const nextChar = csvContent[i + 1];

    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === separator && !inQuotes) {
      currentRow.push(currentField);
      currentField = '';
    } else if (char === '\n' && !inQuotes) {
      currentRow.push(currentField);
      if (currentRow.some(field => field.trim())) {
        rows.push(currentRow);
      }
      currentRow = [];
      currentField = '';
    } else if (char === '\r' && nextChar === '\n' && !inQuotes) {
      currentRow.push(currentField);
      if (currentRow.some(field => field.trim())) {
        rows.push(currentRow);
      }
      currentRow = [];
      currentField = '';
      i++;
    } else {
      currentField += char;
    }
  }

  if (currentField || currentRow.length > 0) {
    currentRow.push(currentField);
    if (currentRow.some(field => field.trim())) {
      rows.push(currentRow);
    }
  }

  return rows;
}

export function parseCSV(csvContent: string): ParsedTransaction[] {
  const rows = parseCSVRows(csvContent);

  if (rows.length === 0) {
    return [];
  }

  const headers = rows[0].map(h => h.trim());
  const transactions: ParsedTransaction[] = [];

  console.log('CSV headers detected:', headers);

  let dateIndex = headers.findIndex(h =>
    /date|jour/i.test(h)
  );
  let labelIndex = headers.findIndex(h =>
    /libell|label|description|op[eé]ration/i.test(h)
  );

  let debitIndex = headers.findIndex(h =>
    /d[eé]bit/i.test(h)
  );
  let creditIndex = headers.findIndex(h =>
    /cr[eé]dit/i.test(h)
  );

  let amountIndex = -1;
  if (debitIndex === -1 && creditIndex === -1) {
    amountIndex = headers.findIndex(h =>
      /montant|amount|valeur/i.test(h)
    );
  }

  if (dateIndex === -1 && headers.length >= 3) {
    dateIndex = 0;
  }
  if (labelIndex === -1 && headers.length >= 3) {
    labelIndex = 1;
  }
  if (amountIndex === -1 && debitIndex === -1 && creditIndex === -1 && headers.length >= 3) {
    amountIndex = 2;
  }

  console.log('Column indices:', { dateIndex, labelIndex, amountIndex, debitIndex, creditIndex });

  const hasDebitCredit = debitIndex !== -1 && creditIndex !== -1;

  if (dateIndex === -1 || labelIndex === -1 || (!hasDebitCredit && amountIndex === -1)) {
    console.error('CSV headers not found:', { dateIndex, labelIndex, amountIndex, debitIndex, creditIndex, headers });
    return [];
  }

  for (let i = 1; i < rows.length; i++) {
    const row = rows[i];

    if (row.some(cell => cell.includes('SOLDE') || cell.includes('Soldes'))) {
      continue;
    }

    const dateStr = row[dateIndex]?.trim();
    const label = row[labelIndex]?.trim().replace(/\s+/g, ' ') || 'Sans libellé';

    let amount: number;

    if (hasDebitCredit) {
      const debitStr = row[debitIndex]?.trim() || '';
      const creditStr = row[creditIndex]?.trim() || '';

      console.log('Processing row (debit/credit):', { dateStr, label, debitStr, creditStr });

      if (!dateStr || (!debitStr && !creditStr)) {
        console.log('Skipping row: missing date or amounts');
        continue;
      }

      if (debitStr) {
        const cleanDebitStr = debitStr.replace(/\s/g, '').replace(/�/g, '').replace(',', '.');
        const debitAmount = parseFloat(cleanDebitStr);
        if (!isNaN(debitAmount)) {
          amount = -Math.abs(debitAmount);
        } else {
          console.log('Skipping row: invalid debit amount', cleanDebitStr);
          continue;
        }
      } else if (creditStr) {
        const cleanCreditStr = creditStr.replace(/\s/g, '').replace(/�/g, '').replace(',', '.');
        const creditAmount = parseFloat(cleanCreditStr);
        if (!isNaN(creditAmount)) {
          amount = Math.abs(creditAmount);
        } else {
          console.log('Skipping row: invalid credit amount', cleanCreditStr);
          continue;
        }
      } else {
        continue;
      }
    } else {
      const amountStr = row[amountIndex]?.trim();

      console.log('Processing row:', { dateStr, label, amountStr });

      if (!dateStr || !amountStr) {
        console.log('Skipping row: missing date or amount');
        continue;
      }

      const cleanAmountStr = amountStr.replace(/\s/g, '').replace(/�/g, '').replace(',', '.');
      amount = parseFloat(cleanAmountStr);
      if (isNaN(amount)) {
        console.log('Skipping row: invalid amount', cleanAmountStr);
        continue;
      }
    }

    let date: string;
    if (dateStr.includes('/')) {
      const [d, m, y] = dateStr.split('/');
      date = `${y.length === 2 ? '20' + y : y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    } else if (dateStr.includes('-')) {
      date = dateStr;
    } else {
      console.log('Skipping row: invalid date format');
      continue;
    }

    transactions.push({
      date,
      label,
      amount
    });
  }

  return transactions;
}
